import React, { useState, useEffect, useRef } from 'react'
import { useSellableKeywordTypeaheadQuery } from '../../common/SellableTypeahead.generated'
import { getSellableUrl } from '#src/services/sellable'
import { useNavigationContext } from '../NavigationContext'
import { Link, useHistory } from 'react-router-dom'
import { serializeSearchQuery } from '#src/services/search'
import { useDebounce } from 'use-debounce'
import useKeyPress from '#src/hooks/useKeyPress'
import useClickOutside from '#src/hooks/useClickOutside'
import ScrollLock, { TouchScrollable } from 'react-scrolllock'
import PageContent from '#src/components/PageContent'
import SearchIcon from '../icons/search.svg'
import ClearIcon from '../icons/clear.svg'
import autosuggestMatch from 'autosuggest-highlight/match'
import autosuggestParse from 'autosuggest-highlight/parse'
import classNames from 'classnames'
import styles from './styles.css'
import { DepartmentContextFragment } from '#src/context/department/DepartmentFragment.generated'

interface SearchProps extends React.HTMLAttributes<HTMLDivElement> {
  department: DepartmentContextFragment
}

const Search: React.FC<SearchProps> = ({ department }) => {
  const navContext = useNavigationContext()
  const history = useHistory()
  const keyboardRef = useRef<HTMLInputElement>(null)

  const topLevelCategory = department.topLevelCategory

  const [inputKeywords, setInputKeywords] = useState('')
  const [queryKeywords, setQueryKeywords] = useState('')

  const debouncedQueryKeywords = useDebounce(queryKeywords, 200)[0]

  useEffect(() => {
    setQueryKeywords(inputKeywords)
  }, [inputKeywords])
  /**
   * Add some handling for escape press and clicking outside to dismiss search
   */
  const escapeKeyPress = useKeyPress('Escape')
  if (escapeKeyPress && navContext?.isSearchVisible) {
    navContext?.onToggleIsSearchVisible(false)
  }

  const panelRef = useClickOutside<HTMLDivElement>(() => {
    if (navContext?.isSearchVisible) {
      navContext?.onToggleIsSearchVisible(false)
    }
  })

  /**
   * Handler for when the form is submitted and we navigate to the selected search entry
   */
  const commitSearch = () => {
    const serializedLocation = serializeSearchQuery({
      keywords: queryKeywords,
      categorySlug: topLevelCategory.slug,
      entryPoint: 'nav-keywords'
    })

    // Reset the search state
    setInputKeywords('')

    navContext?.onToggleIsSearchVisible(false)

    history.push({
      pathname: serializedLocation.pathname,
      search: serializedLocation.searchParams.toString()
    })
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    setInputKeywords(inputValue)
  }

  const { data } = useSellableKeywordTypeaheadQuery({
    variables: {
      query: {
        prefixSearch: debouncedQueryKeywords,
        categoryIds: [topLevelCategory.id]
      },
      first: 10
    },
    skip: queryKeywords.length === 0
  })

  const results = data && data.sellableTypeahead && data.sellableTypeahead.hits

  return (
    <>
      {navContext?.isSearchVisible && <ScrollLock />}

      <div
        className={classNames(styles.panel, {
          [styles.panelVisible]: navContext?.isSearchVisible,
          [styles.hasResults]: results && results.length > 0 && navContext?.isSearchVisible
        })}
      >
        <div
          className={styles.top}
          data-testid="search-panel"
          onClick={e => e.stopPropagation()}
          ref={panelRef}
        >
          <PageContent fullWidth className={styles.content}>
            <div className={styles.entry}>
              <div className={styles.prompt}>What are you looking for?</div>

              <form
                action={''}
                className={styles.form}
                onSubmit={e => {
                  e.preventDefault()

                  commitSearch()
                  keyboardRef.current && keyboardRef.current.blur()
                }}
              >
                <SearchIcon className={styles.searchIcon} data-testid="mobile-search-icon" />
                <input
                  title="Search"
                  ref={keyboardRef}
                  type="search"
                  name="q"
                  placeholder={
                    topLevelCategory ? `Search in ${topLevelCategory.pluralDisplayName}...` : ''
                  }
                  value={inputKeywords}
                  onChange={e => onInputChange(e)}
                  className={styles.keywords}
                  data-testid="search-keyword-input"
                />
                {inputKeywords.length > 0 && (
                  <button
                    type="button"
                    className={styles.clear}
                    onClick={() => {
                      setInputKeywords('')
                    }}
                  >
                    <ClearIcon />
                  </button>
                )}
              </form>
            </div>
            <div className={styles.closeContainer}>
              <div
                className={styles.close}
                onClick={() => {
                  setInputKeywords('')
                  navContext?.onToggleIsSearchVisible(false)
                }}
                data-testid="search-panel-close"
              >
                <span className={styles.closeLabel}>Cancel</span>
              </div>
            </div>
          </PageContent>
        </div>
        <TouchScrollable>
          <div
            className={classNames(styles.resultsContainer, {
              [styles.noResults]: !results || !results.length,
              [styles.focused]: navContext?.isSearchVisible
            })}
          >
            <PageContent fullWidth>
              <ul className={styles.resultsList}>
                {results &&
                  results.map(result => {
                    const keywords = queryKeywords
                    const matches = autosuggestMatch(result.title, keywords)
                    const parts = autosuggestParse(result.title, matches)

                    return (
                      <li className={styles.result} key={result.id}>
                        <Link
                          to={getSellableUrl(result.sellable)}
                          onClick={() => {
                            setInputKeywords('')
                            navContext?.onToggleIsSearchVisible(false)
                          }}
                          className={styles.resultLink}
                        >
                          {parts.map(part => {
                            if (part.highlight) {
                              return <strong key={part.text}>{part.text}</strong>
                            }

                            return <React.Fragment key={part.text}>{part.text}</React.Fragment>
                          })}
                        </Link>
                      </li>
                    )
                  })}
              </ul>
            </PageContent>
          </div>
        </TouchScrollable>
      </div>
    </>
  )
}

export default Search
